import { Link } from "react-router-dom";
import { useScroll, useTransform, motion } from "framer-motion";

import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Globe2,
  BadgeCheck,
  Ship,
} from "lucide-react";

import { IMAGES } from "@/lib/site";

import HeroStats from "./HeroStats";

export default function HeroSection() {

  const { scrollY } = useScroll();

  const heroBgY = useTransform(scrollY, [0, 800], [0, 200]);

  const heroBgScale = useTransform(scrollY, [0, 800], [1.05, 1.15]);

  const heroTextY = useTransform(scrollY, [0, 600], [0, -60]);

  return (
    


      <section data-testid="hero-section" className="relative h-[94vh] min-h-[680px] max-h-[1100px] flex items-center bg-charcoal text-white overflow-hidden">
        {/* HERO */}<motion.div
          style={{ y: heroBgY, scale: heroBgScale }}
          className="absolute inset-0 will-change-transform"
        >
          <img
            src={IMAGES.heroShowroom}
            alt="Premium tile display showroom"
            className="w-full h-[110%] object-cover object-[70%_center] sm:object-[75%_center] md:object-[center_right] contrast-[1.08] saturate-[1.05] brightness-[1.04]"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </motion.div>
        {/* Adaptive overlay — stronger left, lighter right, preserves stands visibility + text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-black/10 md:via-black/15 md:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-charcoal/70" />
        {/* Vignette */}
        <div className="hero-vignette" />
        {/* Golden glow behind heading */}
        <div className="hero-glow left-[-220px] top-[18%]" />
        <div className="grain absolute inset-0 opacity-60" />

        {/* Vertical brand stripe */}
        <div className="absolute left-8 md:left-16 top-32 bottom-32 w-px bg-gradient-to-b from-transparent via-stenzia-gold/40 to-transparent hidden md:block" />

        <motion.div
          style={{ y: heroTextY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
          className="relative max-w-7xl mx-auto px-6 md:px-16 lg:px-20 w-full pt-36 pb-28 md:pt-40 md:pb-32 lg:pt-44 lg:pb-36"
        >
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
              className="hero-h1 font-display font-light text-[42px] sm:text-6xl leading-[1.04] tracking-tighter drop-shadow-[0_4px_30px_rgba(0,0,0,0.55)] max-w-[1100px]"
            >
              Premium Display Systems<br />for <span className="word-glow italic font-normal text-stenzia-gold">Modern</span> <span className="italic font-normal text-stenzia-gold">Showrooms.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.42, ease: [0.22, 0.61, 0.36, 1] }}
              className="hero-lead hero-desc-gap mt-8 md:mt-10 max-w-[480px] text-[15px] text-white/85 leading-[1.75] font-light"
            >
              Engineered for Display Excellence.<br className="hidden sm:block" />
              <span className="block mt-3">Trusted by tile manufacturers, architects and exporters across India, UAE, Europe, USA and Australia.</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.62, ease: [0.22, 0.61, 0.36, 1] }}
              className="hero-cta-gap mt-8 md:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4"
            >
              <Link to="/contact" data-testid="hero-quote-btn" className="group btn-primary">
                Request Quote
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/products" data-testid="hero-products-btn" className="group btn-ghost-light">
                View Products
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* Premium trust signal row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8 }}
              className="hero-trust-gap mt-8 md:mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-[11px] tracking-[0.18em] uppercase text-white/70 font-medium"
            >
              {[
                { Icon: CheckCircle2, label: "Made in India" },
                { Icon: Globe2, label: "Export Ready" },
                { Icon: BadgeCheck, label: "ISO 9001 Certified" },
                { Icon: Ship, label: "Global Shipping" },
              ].map((t, i, arr) => (
                <div key={t.label} className="flex items-center gap-2 md:gap-3">
                  <span className="flex items-center gap-2">
                    <t.Icon size={14} className="text-stenzia-gold" strokeWidth={1.8} />
                    {t.label}
                  </span>
                  {i < arr.length - 1 && <span className="hidden sm:inline-block w-px h-3 bg-white/15" />}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Premium glass stat cards */}
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.95, ease: [0.22, 0.61, 0.36, 1] }}
            >
            <HeroStats />
        </motion.div>

    </motion.div>

        {/* Animated mouse-scroll indicator */}
      </section>
      
  );

}